import { StorageConfigurationServiceType } from './interface';
import {
  CreateStorageParams,
  DeleteStorageParams,
  SetStorageEnabledParams,
  GetMigrationStatusParams,
  IMigrationStatus,
  IStorageConfiguration,
  IValidationResponse,
  MigrationParams,
  ValidateStorageParams,
  UpdateStorageParams,
  CancelMigrationParams
} from '@/services/api/storageConfigurationApiArea/types';
import axiosInstance from '@/api';

/** Класс фабрика StorageConfigurationService который хранит в себе API методы для работы с хранилищами **/
export class StorageConfigurationService implements StorageConfigurationServiceType {
  /** Получить все конфигурации хранилищ **/
  async GetStorageConfigurations(): Promise<IStorageConfiguration[]> {
    try {
      const { data } = await axiosInstance.get<IStorageConfiguration[]>('/v1/StorageConfiguration');

      return data;
    } catch (e) {
      throw new Error(`Error getting storage configurations: ${e}`);
    }
  }

  /** Получить конфигурацию хранилища по ID **/
  async GetStorageConfigurationById(id: number): Promise<IStorageConfiguration> {
    try {
      const { data } = await axiosInstance.get<IStorageConfiguration>(
        `/v1/StorageConfiguration/${id}`
      );

      return data;
    } catch (e) {
      throw new Error(`Error getting storage configuration by id: ${e}`);
    }
  }

  /** Создать новую конфигурацию хранилища **/
  async CreateStorageConfiguration(params: CreateStorageParams): Promise<IStorageConfiguration> {
    try {
      const { data } = await axiosInstance.post<IStorageConfiguration>(
        '/v1/StorageConfiguration',
        params
      );

      return data;
    } catch (e) {
      throw new Error(`Error creating storage configuration: ${e}`);
    }
  }

  /** Изменить статус Enabled хранилища **/
  async SetStorageEnabled(params: SetStorageEnabledParams): Promise<IStorageConfiguration> {
    try {
      const { data } = await axiosInstance.put<IStorageConfiguration>(
        `/v1/StorageConfiguration/${params.id}/Enabled?value=${params.value}`
      );

      return data;
    } catch (e) {
      throw new Error(`Error setting storage enabled status: ${e}`);
    }
  }

  /** Удалить конфигурацию хранилища **/
  async DeleteStorageConfiguration(params: DeleteStorageParams): Promise<boolean> {
    try {
      const { data } = await axiosInstance.delete<boolean>(
        `/v1/StorageConfiguration?id=${params.id}`
      );

      return data;
    } catch (e) {
      throw new Error(`Error deleting storage configuration: ${e}`);
    }
  }

  /** Запустить миграцию данных между хранилищами **/
  async MigrateStorage(params: MigrationParams): Promise<IMigrationStatus> {
    try {
      const { data } = await axiosInstance.post<IMigrationStatus>(
        `/v1/StorageConfiguration/Migration/?from=${params.from}&to=${params.to}`
      );

      return data;
    } catch (e) {
      throw new Error(`Error migrating storage: ${e}`);
    }
  }

  /** Получить статус миграции **/
  async GetMigrationStatus(params: GetMigrationStatusParams): Promise<IMigrationStatus> {
    try {
      const { data } = await axiosInstance.get<IMigrationStatus>(
        `/v1/StorageConfiguration/Migration/Status?id=${params.id}`
      );

      return data;
    } catch (e) {
      throw new Error(`Error getting migration status: ${e}`);
    }
  }

  /** Валидация конфигурации хранилища перед созданием **/
  async ValidateStorageConfiguration(params: ValidateStorageParams): Promise<IValidationResponse> {
    try {
      const { data } = await axiosInstance.post<IValidationResponse>(
        '/v1/StorageConfiguration/Validate',
        params
      );

      return data;
    } catch (e) {
      throw new Error(`Error validating storage configuration: ${e}`);
    }
  }

  /** Обновить конфигурацию хранилища **/
  async UpdateStorageConfiguration(params: UpdateStorageParams): Promise<IStorageConfiguration> {
    try {
      const { id, ...body } = params;
      const { data } = await axiosInstance.put<IStorageConfiguration>(
        `/v1/StorageConfiguration/${id}`,
        body
      );

      return data;
    } catch (e) {
      throw new Error(`Error updating storage configuration: ${e}`);
    }
  }

  /** Отменить миграцию **/
  async CancelMigration(params: CancelMigrationParams): Promise<void> {
    try {
      await axiosInstance.post(`/v1/StorageConfiguration/Migration/Cancel?id=${params.id}`);
    } catch (e) {
      throw new Error(`Error canceling migration: ${e}`);
    }
  }
}
